import React from "react";
import { motion } from "framer-motion";
import {
  FlaskConical,
  ShieldCheck,
  Lightbulb,
  Globe,
  Pill,
  Sparkles,
  Utensils,
  Factory,
} from "lucide-react";

const container = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.25 },
  },
};

const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

export default function SpecialtyChemicals() {
  return (
    <div className="w-full">
      {/* Hero Banner */}
      <div
        className="relative h-[300px] md:h-[400px] w-full bg-cover bg-center flex items-center justify-center"
        style={{
          backgroundImage:
            "url('/images/specialty-chemicals-banner.webp')",
        }}
      >
        <div className="absolute inset-0 bg-black bg-opacity-50"></div>
        <h1 className="relative text-4xl md:text-5xl font-bold text-white text-center">
          Specialty Chemicals
        </h1>
      </div>

      {/* Content Section with Motion */}
      <motion.div
        className="max-w-6xl mx-auto px-4 py-12"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={container}
      >
        {/* Intro */}
        <motion.p className="text-lg text-gray-700 mb-10" variants={fadeInUp}>
          At <span className="font-semibold text-[#0d47a1]">MAC LABS LTD</span>, we
          specialize in developing and supplying high-performance specialty chemicals
          that play a critical role across pharmaceuticals, healthcare, food, cosmetics,
          and industrial applications. With expertise in complex chemical synthesis,
          purification, and customization, we deliver solutions that are safe, consistent,
          and reliable. Every product is engineered with precision, purity, and performance
          at its core.
        </motion.p>

        {/* Our Strengths */}
        <motion.h2 className="text-2xl font-bold text-gray-800 mb-6" variants={fadeInUp}>
          Our Strengths
        </motion.h2>
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          {/* Animated Strength Cards */}
          <motion.div
            className="p-6 border rounded-lg shadow-sm bg-white transition"
            variants={fadeInUp}
            whileHover={{
              scale: 1.06,
              boxShadow: "0px 8px 24px rgba(13,71,161,0.08)",
            }}
            transition={{ type: "spring", stiffness: 200, damping: 20 }}
          >
            <FlaskConical className="w-8 h-8 text-blue-600 mb-3" />
            <h3 className="text-lg font-semibold text-gray-800 mb-2">
              Diverse Applications
            </h3>
            <p className="text-gray-600">
              From pharma intermediates and excipients to cosmetic and food ingredients.
            </p>
          </motion.div>
          <motion.div
            className="p-6 border rounded-lg shadow-sm bg-white transition"
            variants={fadeInUp}
            whileHover={{
              scale: 1.06,
              boxShadow: "0px 8px 24px rgba(13,71,161,0.08)",
            }}
            transition={{ type: "spring", stiffness: 200, damping: 20 }}
          >
            <ShieldCheck className="w-8 h-8 text-green-600 mb-3" />
            <h3 className="text-lg font-semibold text-gray-800 mb-2">
              Quality & Safety First
            </h3>
            <p className="text-gray-600">
              Strict quality control ensures compliance with international standards.
            </p>
          </motion.div>
          <motion.div
            className="p-6 border rounded-lg shadow-sm bg-white transition"
            variants={fadeInUp}
            whileHover={{
              scale: 1.06,
              boxShadow: "0px 8px 24px rgba(13,71,161,0.08)",
            }}
            transition={{ type: "spring", stiffness: 200, damping: 20 }}
          >
            <Lightbulb className="w-8 h-8 text-orange-500 mb-3" />
            <h3 className="text-lg font-semibold text-gray-800 mb-2">
              Innovation-Driven
            </h3>
            <p className="text-gray-600">
              Customized solutions tailored to client and industry needs.
            </p>
          </motion.div>
          <motion.div
            className="p-6 border rounded-lg shadow-sm bg-white transition"
            variants={fadeInUp}
            whileHover={{
              scale: 1.06,
              boxShadow: "0px 8px 24px rgba(13,71,161,0.08)",
            }}
            transition={{ type: "spring", stiffness: 200, damping: 20 }}
          >
            <Globe className="w-8 h-8 text-purple-600 mb-3" />
            <h3 className="text-lg font-semibold text-gray-800 mb-2">
              Global Reach
            </h3>
            <p className="text-gray-600">
              Trusted by partners across healthcare, chemical, and industrial sectors.
            </p>
          </motion.div>
        </div>

        {/* Applications */}
        <motion.h2 className="text-2xl font-bold text-gray-800 mb-6" variants={fadeInUp}>
          Where Our Chemicals Make an Impact
        </motion.h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          <motion.div
            className="p-6 border rounded-lg shadow-sm bg-white transition"
            variants={fadeInUp}
            whileHover={{
              scale: 1.06,
              boxShadow: "0px 8px 24px rgba(13,71,161,0.08)",
            }}
            transition={{ type: "spring", stiffness: 200, damping: 20 }}
          >
            <Pill className="w-8 h-8 text-red-600 mb-3" />
            <h3 className="text-lg font-semibold text-gray-800">
              Pharmaceuticals & Healthcare
            </h3>
            <p className="text-gray-600">Building blocks for advanced medicines.</p>
          </motion.div>
          <motion.div
            className="p-6 border rounded-lg shadow-sm bg-white transition"
            variants={fadeInUp}
            whileHover={{
              scale: 1.06,
              boxShadow: "0px 8px 24px rgba(13,71,161,0.08)",
            }}
            transition={{ type: "spring", stiffness: 200, damping: 20 }}
          >
            <Sparkles className="w-8 h-8 text-pink-500 mb-3" />
            <h3 className="text-lg font-semibold text-gray-800">
              Cosmetics & Personal Care
            </h3>
            <p className="text-gray-600">
              Safe, effective ingredients for beauty & wellness.
            </p>
          </motion.div>
          <motion.div
            className="p-6 border rounded-lg shadow-sm bg-white transition"
            variants={fadeInUp}
            whileHover={{
              scale: 1.06,
              boxShadow: "0px 8px 24px rgba(13,71,161,0.08)",
            }}
            transition={{ type: "spring", stiffness: 200, damping: 20 }}
          >
            <Utensils className="w-8 h-8 text-yellow-600 mb-3" />
            <h3 className="text-lg font-semibold text-gray-800">Food & Beverages</h3>
            <p className="text-gray-600">
              Preservatives and additives for safety & shelf-life.
            </p>
          </motion.div>
          <motion.div
            className="p-6 border rounded-lg shadow-sm bg-white transition"
            variants={fadeInUp}
            whileHover={{
              scale: 1.06,
              boxShadow: "0px 8px 24px rgba(13,71,161,0.08)",
            }}
            transition={{ type: "spring", stiffness: 200, damping: 20 }}
          >
            <Factory className="w-8 h-8 text-gray-700 mb-3" />
            <h3 className="text-lg font-semibold text-gray-800">Industrial Chemicals</h3>
            <p className="text-gray-600">
              Specialty inputs for dyes, pigments, and performance products.
            </p>
          </motion.div>
        </div>

        {/* Closing */}
        <motion.p className="text-gray-700 font-medium" variants={fadeInUp}>
          With our Specialty Chemicals,{" "}
          <span className="font-semibold">MAC LABS LTD</span> continues to deliver
          innovative, high-quality, and sustainable solutions that empower industries
          and improve lives worldwide.
        </motion.p>
      </motion.div>
    </div>
  );
}
